import { useState } from "react";
import {
  EnumActivityIndicatorState,
  EnumEmployeeShiftState,
  ShiftPlanEmployee,
  UserModel,
} from "../Managers/dataStructures";
import { ActivityIndicator } from "../Managers/utilityComponents";
import { calculateEmployeeElapsedTime } from "../Managers/processManager";
import { getLanguage } from "../Managers/dataProvider";

export interface TLEmployeeDialogProps {
  shift_plan_employee: ShiftPlanEmployee;
  startPauseShift: (
    employee: ShiftPlanEmployee,
    callback: (isActivityRunning: EnumActivityIndicatorState) => void
  ) => void;
  endShift: (
    employee: ShiftPlanEmployee,
    callback_: (isActivity: EnumActivityIndicatorState) => void
  ) => void;
  closeDialog: () => void;
}

export default function TLEmployeeDialog(props: TLEmployeeDialogProps) {
  const isEnglish = getLanguage();
  const [activityIndicatorState, setActivityIndicatorState] =
    useState<EnumActivityIndicatorState>(
      EnumActivityIndicatorState.NOT_RUNNING
    );

  const user: UserModel = props.shift_plan_employee.user;
  const currentState = props.shift_plan_employee.current_state;
  const isDisabled: boolean =
    activityIndicatorState === EnumActivityIndicatorState.NOT_RUNNING
      ? false
      : true;

  async function startPuaseShift() {
    if (
      activityIndicatorState === EnumActivityIndicatorState.END_SHIFT ||
      activityIndicatorState === EnumActivityIndicatorState.START_STOP_SHIFT
    ) {
      return;
    }
    props.startPauseShift(
      props.shift_plan_employee,
      (isActivityRunning: EnumActivityIndicatorState) => {
        setActivityIndicatorState(isActivityRunning);
      }
    );
  }

  async function endShift() {
    if (
      activityIndicatorState === EnumActivityIndicatorState.END_SHIFT ||
      activityIndicatorState === EnumActivityIndicatorState.START_STOP_SHIFT
    ) {
      return;
    }
    props.endShift(
      props.shift_plan_employee,
      (isActivityRunning: EnumActivityIndicatorState) => {
        setActivityIndicatorState(isActivityRunning);
        if (isActivityRunning === EnumActivityIndicatorState.NOT_RUNNING) {
          props.closeDialog();
        }
      }
    );
  }

  return (
    <div className="relative isolate">
      <div className="flex justify-center max-w-7xl">
        <div className="relative transform overflow-hidden rounded-lg bg-white px-4 pb-4 pt-5 text-left shadow-xl transition-all sm:my-8 sm:w-full sm:max-w-lg sm:p-6">
          <div>
            <button
              type="button"
              onClick={props.closeDialog}
              className="absolute right-4 top-4 text-gray-400 hover:text-gray-500 sm:right-6 sm:top-8 md:right-6 md:top-6 lg:right-8 lg:top-8"
            >
              <span className="sr-only">
                {isEnglish ? "Close" : "Schließen"}
              </span>
              <svg
                className="h-6 w-6"
                fill="none"
                viewBox="0 0 24 24"
                strokeWidth="1.5"
                stroke="currentColor"
                aria-hidden="true"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  d="M6 18L18 6M6 6l12 12"
                />
              </svg>
            </button>
            <div className="bg-white px-4 py-5 sm:px-6">
              <div className="flex space-x-3">
                <div className="flex-shrink-0">
                  <img
                    className="h-14 w-14 rounded-full"
                    src={user.profile_pic_url}
                    alt=""
                  />
                </div>
                <div className="min-w-0 flex-1">
                  <p className=" font-semibold text-gray-900">
                    {user.first_name + " " + user.last_name}
                  </p>
                  <p className=" text-gray-500">{user.email_id}</p>
                </div>
              </div>
            </div>
            <div className="mt-3 text-left sm:mt-5">
              <div className="mt-2">
                <p className=" font-semibold text-gray-900">
                  {(isEnglish ? "Current hours: " : "Aktuelle Arbeitszeit: ") +
                    calculateEmployeeElapsedTime(
                      props.shift_plan_employee.array_shift_entries
                    )}
                </p>
              </div>
            </div>
          </div>
          <div className=" sm:mt-6 sm:grid sm:grid-flow-row-dense sm:grid-cols-2 sm:gap-3">
            <button
              onClick={startPuaseShift}
              disabled={isDisabled}
              type="button"
              className="inline-flex items-center gap-x-2 rounded-md bg-indigo-600 px-3.5 py-2.5 text-sm font-semibold text-white shadow-sm hover:bg-indigo-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600"
            >
              {activityIndicatorState ===
              EnumActivityIndicatorState.START_STOP_SHIFT
                ? ActivityIndicator(0)
                : currentState === EnumEmployeeShiftState.WORKING
                ? PauseIcon()
                : PlayIcon()}
              {currentState === EnumEmployeeShiftState.WORKING
                ? isEnglish
                  ? "Pause Timer"
                  : "Pause beginnen"
                : isEnglish
                ? "Start Timer"
                : "Arbeit beginnen"}
            </button>
            <button
              onClick={endShift}
              disabled={isDisabled}
              type="button"
              className="inline-flex items-center gap-x-2 rounded-md  px-3.5 py-2.5 text-sm font-semibold text-white shadow-sm hover:bg-red-500 bg-red-600 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600"
            >
              {activityIndicatorState === EnumActivityIndicatorState.END_SHIFT
                ? ActivityIndicator(0)
                : null}
              {isEnglish ? "End Shift" : "Arbeit beenden"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

function PlayIcon() {
  return (
    <svg
      className="-ml-0.5 h-7 w-7"
      viewBox="0 0 512 512"
      fill="none"
      strokeLinejoin="miter"
      aria-hidden="true"
      color="#FFFFFF"
      stroke="#FFFFFF"
      strokeWidth="1"
      strokeLinecap="square"
    >
      <g fill="#FFFFFF">
        <path d="m354.2,247.4l-135.1-92.4c-4.2-3.1-15.4-3.1-16.3,8.6v184.8c1,11.7 12.4,11.9 16.3,8.6l135.1-92.4c3.5-2.1 8.3-10.7 0-17.2zm-130.5,81.3v-145.4l106.1,72.7-106.1,72.7z" />
        <path d="M256,11C120.9,11,11,120.9,11,256s109.9,245,245,245s245-109.9,245-245S391.1,11,256,11z M256,480.1    C132.4,480.1,31.9,379.6,31.9,256S132.4,31.9,256,31.9S480.1,132.4,480.1,256S379.6,480.1,256,480.1z" />
      </g>
    </svg>
  );
}

function PauseIcon() {
  return (
    <svg
      className="-ml-0.5 h-7 w-7"
      viewBox="0 0 24 24"
      fill="none"
      strokeLinejoin="miter"
      aria-hidden="true"
      color="#FFFFFF"
      stroke="#FFFFFF"
      strokeWidth="1"
      strokeLinecap="square"
    >
      <path
        //d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.857-9.809a.75.75 0 00-1.214-.882l-3.483 4.79-1.88-1.88a.75.75 0 10-1.06 1.061l2.5 2.5a.75.75 0 001.137-.089l4-5.5z"
        d="M12 22C17.5228 22 22 17.5228 22 12C22 6.47715 17.5228 2 12 2C6.47715 2 2 6.47715 2 12C2 17.5228 6.47715 22 12 22Z"
        clipRule="evenodd"
        fillRule="evenodd"
      />
      <path fillRule="evenodd" d="M8 8V16H10V8H8Z" clipRule="evenodd" />
      <path fillRule="evenodd" clipRule="evenodd" d="M14 8V16H16V8H14Z" />
    </svg>
  );
}
